import { useTranslation } from 'react-i18next';
import DoneImg from '../../assets/icons/step-done.svg?react';
import MarkImg from '../../assets/icons/step-mark.svg?react';

import './Stepper.less';

export interface StepperStep<K extends string> {
  key: K;
  label: string;
}

export interface StepperProps<K extends string> {
  steps: readonly StepperStep<K>[];
  // `NoInfer` pins `K` to the keys carried by `steps`. Without it TypeScript
  // treats `current` as a second inference site and widens `K` to include it,
  // which silently accepts a key that is not in the list.
  current: NoInfer<K>;
  className?: string;
}

type StepState = 'done' | 'current' | 'upcoming';

// Screen readers otherwise get colour and icon shape only. Keyed for
// translation with an English fallback so no locale bundle has to change
// before the component can ship.
const STATUS_TEXT: Record<StepState, { key: string; fallback: string }> = {
  done: { key: 'stepper.status.completed', fallback: 'completed' },
  current: { key: 'stepper.status.current', fallback: 'current step' },
  upcoming: { key: 'stepper.status.upcoming', fallback: 'not started' },
};

const stateAt = (index: number, currentIndex: number): StepState => {
  if (index < currentIndex) {
    return 'done';
  }
  if (index === currentIndex) {
    return 'current';
  }
  return 'upcoming';
};

const Stepper = <K extends string>({ steps, current, className }: StepperProps<K>) => {
  const { t } = useTranslation();
  const currentIndex = steps.findIndex(step => step.key === current);

  return (
    <ol className={['stepper', className].filter(Boolean).join(' ')}>
      {steps.map((step, index) => {
        const state = stateAt(index, currentIndex);
        const status = STATUS_TEXT[state];

        return (
          <li
            key={step.key}
            className={`stepper-step stepper-step--${state}`}
            aria-current={state === 'current' ? 'step' : undefined}
          >
            <span className="stepper-marker">{state === 'done' ? <DoneImg /> : <MarkImg />}</span>
            <span className="stepper-label">{step.label}</span>
            <span className="stepper-status">
              {t(status.key, { defaultValue: status.fallback })}
            </span>
          </li>
        );
      })}
    </ol>
  );
};

export default Stepper;
